import React, { useState } from 'react';
import intl from 'react-intl-universal';
import { Link } from 'react-router-dom';
import { defaultRowCount } from 'constants/listViewLayoutConstants';
import { DWHUserCallsResultType } from 'interfaces/Dashboard';
import { exportToCsv } from 'utils/csv-utils';
import { parseRoomSystemDrillDownDataForExport } from 'utils/exportUtils';
import ListViewComponent from 'components/ListView/ListViewComponent';
import ListViewLayout from 'components/ListViewLayout/ListViewLayout';
import DrilldownHeader from '../Common/DrilldownHeader2';
import { useQuery } from 'react-apollo';
import RoomTotalsQuery from 'queries/dashboard/RoomTotalsQuery.gql';
import { RowInfo } from 'react-table';
import _get from 'lodash/get';
import { Moment } from 'moment';
import SearchBar from 'components/Search/SearchBar';
import { SortDirection } from 'constants/queryConstants';
import { SetSortInterface } from 'interfaces/Tables';

interface Props {
  dateStart: Moment;
  dateEnd: Moment;
  searchString: string;
  pageNumber: number;
  onSelect: Function;
  sort?: SetSortInterface;
  search?: string;
}

const RoomSystemsTable = (props: Props) => {
  const defaultSort: SetSortInterface = {
    sortBy: 'totalMinutes',
    order: SortDirection.DESC
  };
  const initialSort = _get(props, 'sort', defaultSort);
  const initialFilterString = _get(props, 'search');
  const [sort, setSort] = useState<SetSortInterface>(initialSort);
  const [search, setSearch] = useState(initialFilterString);
  const { data, loading, refetch, error } = useQuery(RoomTotalsQuery, {
    variables: {
      dateStart: props.dateStart.format('YYYY-MM-DD'),
      dateEnd: props.dateEnd.format('YYYY-MM-DD')
    },
    fetchPolicy: 'network-only'
  });

  const handleExportToCsv = (eData: DWHUserCallsResultType[]) => {
    const exportData = eData.sort(
      (a: DWHUserCallsResultType, b: DWHUserCallsResultType) => {
        return b.totalMinutes - a.totalMinutes;
      }).map(parseRoomSystemDrillDownDataForExport);
    exportToCsv(intl.get('roomSystems'), exportData);
  };

  const getFilteredData = (fData: DWHUserCallsResultType[]) => {
    if (search) {
      return fData.filter(record => record.name.toLowerCase().indexOf(search.toLowerCase()) !== -1);
    }
    return fData;
  };

  const allRows = _get(data, 'roomTotalsTable', []);
  const records = getFilteredData(allRows);
  return (
    <ListViewLayout
      error={_get(error, 'message', undefined)}
      hideHeader={true}
      isFetching={loading}
      title={intl.get('headerRoomSystemTotals')}
    >
      <SearchBar
        setFilterString={(newString) => {
          setSearch(newString);
        }}
        initialValue={initialFilterString}
      />

      <DrilldownHeader
        fetchData={refetch}
        onExportClick={() => handleExportToCsv(records)}
      />

      {loading ? <div /> :
        <ListViewComponent
          data={records}
          columns={
            [{
              Header: intl.get('tableHeaderName'),
              accessor: 'name',
              Cell: (row: RowInfo) => (
                <Link
                  title={row.original.name}
                  to={{
                    pathname: `/drilldown/usersVideos/rooms2/${encodeURIComponent(row.original.userId)}`,
                    state: {
                      sort,
                      search
                    }
                  }}
                >
                  {row.original.name}
                </Link>
              )
            }, {
              Header: intl.get('minutes'),
              accessor: 'totalMinutes',
            }, {
              Header: intl.get('calls'),
              accessor: 'totalCalls'
            }]
          }
          showPagination={false}
          maxRows={defaultRowCount}
          pageNumber={props.pageNumber}
          defaultSorted={[
            {
              id: sort.sortBy,
              desc: sort.order === SortDirection.DESC
            }
          ]}
          onSortedChange={(newSorted) => {
            setSort({
              sortBy: newSorted[0].id,
              order: newSorted[0].desc ? SortDirection.DESC : SortDirection.ASC
            });
          }}
          defaultPageSize={allRows.length}
        />
      }
    </ListViewLayout>
  );
};

export default RoomSystemsTable;
